<template>
  <div @dragover="dragoverEvent" class="w-full relative">
    <Header
      :count="myMediaCount ? myMediaCount : 0"
      @createFolder="createFolder"
      @displayDownloadSection="displayDownloadSection"
    />
    <div class="w-full px-6 h-full md:h-20 border-b">
      <div
        class="h-full w-full flex justify-start md:justify-center items-center mx-auto relative"
      >
        <div
          class="md:absolute left-0 inset-y-0 flex flex-col md:flex-row items-start md:items-center"
        >
          <MyMediaSearchInput
            @search="addFilter('search', $event)"
            input-classes="bg-smoke"
            class="mr-2 md:mr-5 mt-3 mb-2 md:mt-0 md:mb-0"
          />
          <MyMediaFilters
            @change="runSearch"
            :shownFilters="shownFilters"
            :activeFilters="activeFilters"
          />
        </div>
        <div
          class="absolute right-0 md:inset-y-0 top-3 md:top-0 flex items-center text-xs"
        >
          <button
            @click="changeViewType('Grid')"
            class="btn p-2 flex items-center justify-center rounded-full text-payne hover:opacity-60 hover:text-opacity-60 transition"
          >
            Grid
            <GridIcon class="ml-1" />
          </button>
          <p>/</p>
          <button
            @click="changeViewType('List')"
            class="btn p-2 flex items-center justify-center rounded-full text-payne hover:opacity-60 hover:text-opacity-60 transition"
          >
            List
            <ListIcon class="ml-1" />
          </button>
        </div>
      </div>
    </div>
    <Transition name="fade">
      <div class="relative">
        <div
          v-if="showAsGrid"
          :class="
            showDetails
              ? 'w-2/3 2xl:w-9/12 grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6'
              : 'w-full grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6'
          "
          class="p-5 grid gap-8 transition-all ease-out"
        >
          <Folder
            v-for="item in allFolders"
            :key="item.id"
            :folder="item"
            @open="goToFolder"
            @openRenameModal="openManageFolderModal"
            @openDeleteModal="openDeleteFolderModal"
          />
          <ChurchItemComponent
            @removeFromRoot="removeFromRoot"
            :withFavorite="true"
            @rename="openRenameItemrModal(item)"
            @click="selectMedia(item)"
            v-for="item in allItems"
            :key="item.id"
            :item="item"
            @deleteItem="openDeleteItemModal"
          />
        </div>
      </div>
    </Transition>
    <Transition name="opacity">
      <div v-if="!showAsGrid">
        <MediaTable>
          <template slot="row" class="outer">
            <MediaRow
              v-for="item in allFolders"
              :key="item.id"
              :item="item"
              @open="goToFolder"
              :isFolder="true"
              @openDeleteModal="openDeleteFolderModal"
              @openRenameModal="openManageFolderModal"
            />
            <MediaRow
              @click="selectMedia(item)"
              v-for="item in allItems"
              :key="item.id"
              :item="item"
              @deleteItem="openDeleteItemModal"
              @openMoveModal="openMoveModal"
              @openDetails="selectMedia(item)"
            />
          </template>
        </MediaTable>
      </div>
    </Transition>
    <infinite-loading ref="infiniteLoader" @infinite="loadMore">
      <div slot="spinner" class="block">
        <template v-if="showAsGrid">
          <div class="ml-4 items-grid mt-4">
            <item-skeleton
              v-for="item in 4"
              :key="`item-skeleton-item-${item}`"
            />
          </div>
        </template>
        <template v-else>
          <row-skeleton v-for="item in 4" :key="`row-skeleton-item-${item}`" />
        </template>
      </div>
      <div slot="no-more" class="my-8 text-sm font-medium text-payneFire">
        {{ emptyText }}
      </div>
      <div
        v-if="allFolders && allFolders.length === 0"
        slot="no-results"
        class="my-8 text-sm font-medium text-payneFire"
      >
        No items to display
      </div>
    </infinite-loading>
    <Transition name="details">
      <MediaDetails
        @deleteItem="openDeleteItemModal"
        @close="showDetails = false"
        @removeFromRoot="removeFromRoot"
        v-if="showDetails"
        :selectedMedia="selectedMedia"
      />
    </Transition>
    <portal to="destination">
      <div
        v-if="manageFolderModalOpen"
        class="flex justify-center items-center fixed z-20 bg-gray-500 bg-opacity-30 h-screen w-full"
      >
        <RenameModal
          v-on:closeModal="closeManageFolderModal"
          :item="folderToManage"
          @rename="renameFolder"
        />
      </div>
      <div
        v-if="renameItemModalOpen"
        class="flex justify-center items-center fixed z-20 bg-gray-500 bg-opacity-30 h-screen w-full"
      >
        <RenameModal
          v-on:closeModal="closeItemRenameModal"
          :item="itemToManage"
          @rename="renameItem"
        />
      </div>
      <div
        v-if="confirmDeleteModalOpen"
        class="flex justify-center items-center fixed z-20 bg-gray-500 bg-opacity-30 h-screen w-full"
      >
        <ConfirmDeleteModal
          v-on:closeModal="closeConfirmDeleteModal"
          :folder="folderToManage"
          @delete="deleteFolder"
        />
      </div>
    </portal>
    <div
      v-show="
        showUploadSection ||
        (currentUploadingItems && currentUploadingItems.length > 0) ||
        currentUploadingErrors > 0
      "
      class="absolute inset-0 flex items-end h-screen justify-center p-10"
    >
      <Transition name="fade">
        <div
          v-on-clickaway="forceCloseUploadZone"
          v-show="showUploadSection"
          class="cursor-pointer absolute z-40 bottom-20 md:bottom-4 right-5 left-5"
        >
          <UploadSection @close="closeUploadSection" />
        </div>
      </Transition>
      <Transition name="fade">
        <div
          v-if="
            (currentUploadingItems && currentUploadingItems.length > 0) ||
            currentUploadingErrors > 0
          "
          class="cursor-pointer absolute z-40 bottom-20 md:bottom-4 right-5 left-5"
        >
          <UploadResults
            :currentUploading="currentUploadingItems"
            :uploadErrors="uploadErrors"
            :uploadSuccess="uploadSuccess"
            @changeCurrentUploadingErrors="changeCurrentUploadingErrors"
            @removeFromCurrent="removeFromCurrent"
          />
        </div>
      </Transition>
    </div>
    <portal to="destination">
      <div
        v-show="confirmDeleteItemModalOpen"
        class="flex justify-center items-center fixed z-20 bg-gray-500 bg-opacity-30 h-screen w-full"
      >
        <ConfirmDeleteItemModal
          v-on:closeModal="confirmDeleteItemModalOpen = false"
          :item="itemToManage"
          @delete="deleteItem"
        />
      </div>
    </portal>
    <portal to="destination">
      <div
        v-if="showMoveModal"
        class="flex justify-center items-center fixed z-20 bg-gray-500 bg-opacity-30 h-screen w-full"
      >
        <MoveItemModal
          v-on="$listeners"
          @closeModal="showMoveModal = false"
          :item="itemToManage"
        />
      </div>
    </portal>
  </div>
</template>

<script>
import InfiniteLoading from "vue-infinite-loading";
import AppLayout from "@/layout/app-layout";
import MyMediaSearchInput from "@/components/shared/MyMediaSearchInput";
import GridIcon from "@/components/svg/GridIcon";
import ListIcon from "@/components/svg/ListIcon";
import Folder from "@/components/shared/Folder";
import ChurchItemComponent from "@/components/my-media/ChurchItemComponent";
import MediaTable from "@/components/my-media/Table";
import MediaRow from "@/components/my-media/Table/partials/Row";
import MediaDetails from "@/components/my-media/MediaDetails";
import ConfirmDeleteModal from "@/components/my-media/ConfirmDeleteModal";
import MyMediaFilters from "@/components/my-media/MyMediaFilters.vue";
import RowSkeleton from "@/components/shared/RowSkeleton";
import ItemSkeleton from "@/components/shared/ItemSkeleton";
import UploadSection from "@/components/my-media/Upload/UploadSection.vue";
import UploadResults from "@/components/my-media/Upload/UploadResults.vue";
import ConfirmDeleteItemModal from "@/components/my-media/ConfirmDeleteItemModal";
import Header from "@/components/my-media/Header";
import RenameModal from "@/components/my-media/RenameModal.vue";
import MoveItemModal from "@/components/my-media/MoveItemModal.vue";
import { mixin as clickaway } from "vue-clickaway";

export default {
  name: "MyMediaDashboard",
  layout: AppLayout,

  mixins: [clickaway],

  data() {
    return {
      showAsGrid: this.viewType !== "List",
      showDetails: false,
      selectedMedia: null,
      activeFilters: this.filters || {},
      manageFolderModalOpen: false,
      folderToManage: null,
      confirmDeleteModalOpen: false,
      allItems: this.items.data,
      allFolders: this.folders.data,
      showUploadSection: false,
      uploadErrors: null,
      uploadSuccess: null,
      currentUploadingItems: [],
      currentUploadingErrors: 0,
      confirmDeleteItemModalOpen: false,
      itemToManage: null,
      renameItemModalOpen: false,
      dragoverTime: null,
      showMoveModal: false,
    };
  },

  components: {
    MyMediaSearchInput,
    GridIcon,
    ListIcon,
    Folder,
    ChurchItemComponent,
    MediaTable,
    MediaRow,
    MediaDetails,
    ConfirmDeleteModal,
    MyMediaFilters,
    InfiniteLoading,
    RowSkeleton,
    ItemSkeleton,
    UploadSection,
    UploadResults,
    ConfirmDeleteItemModal,
    Header,
    RenameModal,
    MoveItemModal,
  },

  props: {
    items: Object,
    folders: Object,
    filters: Object,
    flash: Object,
    viewType: String,
  },

  computed: {
    emptyText() {
      return this.folders &&
        this.folders.length === 0 &&
        this.allItems &&
        this.allItems.length === 0
        ? "No items to display"
        : "No more items to display";
    },

    myMediaCount() {
      return this.$page.props.count ?? 0;
    },

    shownFilters() {
      let shownFilters = { ...this.activeFilters };
      if (shownFilters.account) {
        let pagesNames = this.getPagesNames(shownFilters.account);
        shownFilters.account = pagesNames;
      }

      return shownFilters;
    },

    parsedFilters() {
      const filters = {};

      Object.keys(this.activeFilters).forEach((key) => {
        if (this.activeFilters[key] === null) {
          delete this.activeFilters[key];
          return;
        }

        let keyName = `filter[${key}]`;
        filters[keyName] = this.activeFilters[key];
      });

      filters["viewType"] = this.showAsGrid ? "Grid" : "List";

      return filters;
    },
  },

  mounted() {
    if (
      (!this.items.links || !this.items.links.next) &&
      (!this.folders.links || !this.folders.links.next)
    ) {
      this.$refs.infiniteLoader.stateChanger.loaded();
      this.$refs.infiniteLoader.stateChanger.complete();
    }
  },

  methods: {
    dragoverEvent() {
      this.showUploadSection = true;
      this.disableScroll();
      clearTimeout(this.dragoverTime);
      this.dragoverTime = setTimeout(() => {
        this.forceCloseUploadZone();
      }, 1500);
    },

    addFilter(type, value) {
      this.$set(this.activeFilters, type, value);
      this.runSearch();
    },

    disableScroll() {
      document.documentElement.style.overflow = "hidden";
    },

    enableScroll() {
      document.documentElement.style.overflow = "auto";
    },

    closeUploadSection(files) {
      this.currentUploadingItems = [...files];
      this.showUploadSection = false;
    },

    forceCloseUploadZone() {
      if (this.showUploadSection) {
        this.enableScroll();
        this.showUploadSection = false;
      }
    },

    openMoveModal(item) {
      this.showMoveModal = true;
      this.itemToManage = item;
      if (this.includesVideo) {
        this.$refs.video.pause();
      }
    },

    changeCurrentUploadingErrors(value) {
      this.currentUploadingErrors = value;
    },

    displayDownloadSection() {
      setTimeout(() => {
        if (
          this.currentUploadingItems.length === 0 &&
          this.currentUploadingErrors === 0
        ) {
          this.showUploadSection = true;
          this.disableScroll();
        } else {
          this.$toast.error("Please wait until the upload finishes");
          this.flash.error = "";
        }
      }, 50);
    },

    removeFromCurrent(requestId) {
      this.currentUploadingItems = this.currentUploadingItems.filter(
        (item) => item.request_id != requestId
      );
      if (this.currentUploadingItems.length === 0) {
        this.enableScroll();
      }
    },

    hideDownloadSection() {
      this.showUploadSection = false;
    },

    openManageFolderModal(folder) {
      this.folderToManage = folder;
      this.manageFolderModalOpen = true;
    },

    openRenameItemrModal(item) {
      this.itemToManage = item;
      this.renameItemModalOpen = true;
    },

    closeManageFolderModal() {
      this.manageFolderModalOpen = false;
    },
    closeItemRenameModal() {
      this.renameItemModalOpen = false;
    },

    renameItem(name, item) {
      try {
        this.$inertia.put(
          this.$route("church-media.items.update", item.id),
          {
            name: name,
          },
          {
            preserveScroll: true,
          }
        );
        const index = this.allItems.findIndex(
          (existingItem) => existingItem.id === item.id
        );
        if (index !== -1) {
          this.allItems[index].name = name;
        }
      } catch (ex) {
        this.flash.error = "Only folders owners can rename them";
      }
    },

    removeFromRoot(item) {
      this.allItems = this.allItems.filter(
        (existingItem) => existingItem !== item
      );
    },

    renameFolder(folderName, folder) {
      try {
        const route = this.$route("church-media.folders.updateName", folder.id);
        this.$inertia.put(
          route,
          {
            name: folderName,
            redirectRoute: "my-media",
          },
          {
            preserveScroll: true,
          }
        );
        const index = this.allFolders.findIndex(
          (existingItem) => existingItem.id === folder.id
        );
        if (index !== -1) {
          this.allFolders[index].name = folderName;
        }
      } catch (ex) {
        this.flash.error = "Only folders owners can rename them";
      }
    },

    createFolder(folderName) {
      try {
        const route = this.$route("church-media.folders.store");
        this.$inertia.post(
          route,
          {
            name: folderName,
          },
          {
            preserveScroll: true,
          }
        );
      } catch (ex) {
        this.flash.error =
          "There was a problem creating the folder, please try again later";
      }
    },

    selectMedia(selectedItem) {
      this.showDetails = true;
      this.selectedMedia = selectedItem;
    },

    changeViewType(typeView) {
      typeView === "Grid"
        ? (this.showAsGrid = true)
        : (this.showAsGrid = false);
    },

    closeConfirmDeleteModal() {
      this.confirmDeleteModalOpen = false;
    },

    openDeleteFolderModal(folder) {
      this.folderToManage = folder;
      this.confirmDeleteModalOpen = true;
    },

    deleteFolder(folder) {
      try {
        this.$inertia.delete(
          this.$route("church-media.folders.destroy", folder),
          { preserveScroll: true }
        );
        this.allFolders = this.allFolders.filter(
          (existingItem) => existingItem != folder
        );
      } catch (ex) {
        this.flash.error = "Only folders owners can delete them";
      }
    },

    runSearch() {
      this.$inertia.get(this.$route("my-media"), this.parsedFilters, {
        onStart: (visit) => (this.working = true),
        onFinish: (visit) => (this.working = false),
        preserveScroll: true,
      });
    },

    goToFolder(id) {
      this.$inertia.get(this.$route("my-media.show", id), this.parsedFilters, {
        onStart: (visit) => (this.working = true),
        onFinish: (visit) => (this.working = false),
        preserveScroll: true,
      });
    },

    loadMore($state) {
      if (
        (!this.items.links || !this.items.links.next) &&
        (!this.folders.links || !this.folders.links.next)
      ) {
        return;
      }

      if (this.items.links.next) {
        this.$inertia.visit(this.items.links.next, {
          preserveState: true,
          preserveScroll: true,
          only: ["items"],
          onFinish: () => {
            $state.loaded();
            if (!this.items.links.next && !this.folders.links.next) {
              $state.complete();
            }
          },
        });
      } else {
        this.$inertia.visit(this.folders.links.next, {
          preserveState: true,
          preserveScroll: true,
          only: ["folders"],
          onFinish: () => {
            $state.loaded();
            if (!this.items.links.next && !this.folders.links.next) {
              $state.complete();
            }
          },
        });
      }
    },
    openDeleteItemModal(item) {
      (this.itemToManage = item), (this.confirmDeleteItemModalOpen = true);
    },
    deleteItem(item) {
      try {
        this.$inertia.delete(this.$route("church-media.items.destroy", item), {
          preserveScroll: true,
        });
        this.allItems = this.allItems.filter(
          (existingItem) => existingItem != item
        );
      } catch (ex) {
        this.flash.error = "Only folders owners can delete them";
      }
    },

    deleteItemFromDetails() {
      this.deleteItem(this.selectedMedia);
      this.showDetails = false;
    },
  },

  watch: {
    items(items) {
      items.data.forEach((item) => {
        const index = this.allItems.findIndex(
          (existingItem) => existingItem.id === item.id
        );
        if (index === -1) {
          this.allItems.push(item);
        }
      });
    },

    folders(folders) {
      folders.data.forEach((item) => {
        const index = this.allFolders.findIndex(
          (existingItem) => existingItem.id === item.id
        );
        if (index === -1) {
          this.allFolders.push(item);
        }
      });
    },

    flash: {
      handler(flash) {
        if (flash.success && flash.success !== "") {
          this.$toast.success(flash.success);
        }
        if (flash.error && flash.error !== "") {
          this.$toast.error(flash.error);
          this.flash.error = "";
        }
        if (flash.uploadError) {
          this.uploadErrors = flash.uploadError;
        }
        if (flash.uploadSuccess) {
          this.uploadSuccess = flash.uploadSuccess;
        }
      },
      deep: true,
    },
  },
};
</script>

<style>
.fade-enter-active {
  animation: fade-in 0.5s;
}
.fade-leave-active {
  animation: fade-in 0.5s reverse;
}
.opacity-enter-active {
  animation: opacity-in 1s;
}
.opacity-leave-active {
  animation: opacity-in 0.4s reverse;
}
.details-enter-active {
  animation: details-in 0.5s;
}
.details-leave-active {
  animation: details-in 0.1s reverse;
}
@keyframes fade-in {
  0% {
    transform: translateX(40px);
    opacity: 0;
  }
  30% {
    opacity: 0;
  }
  100% {
    transform: translateX(0px);
    opacity: 1;
  }
}
@keyframes opacity-in {
  0% {
    transform: translateX(-40px);
    opacity: 0;
  }
  50% {
    transform: translateX(-40px);
    opacity: 0;
  }
  100% {
    transform: translateX(0px);
    opacity: 1;
  }
}
@keyframes details-in {
  0% {
    transform: translateX(40px);
    opacity: 0;
  }
  100% {
    transform: translateX(0px);
    opacity: 1;
  }
}
</style>
